import { FC, FormEvent, useCallback, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, Card, CardBody, Flex, Input, Text, VStack } from '@bonusly/design-system';

import { useGranolaConnect, useGranolaDisconnect, useGranolaStatus } from '../../queries/granola';

export const GranolaConnectCard: FC = () => {
	const { data: status, isLoading: isLoadingStatus } = useGranolaStatus();
	const { mutateAsync: connect, isLoading: isConnecting } = useGranolaConnect();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useGranolaDisconnect();
	const { track } = useAnalytics();

	const [apiKey, setApiKey] = useState('');
	const [error, setError] = useState('');

	const handleConnect = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			setError('');
			track(AnalyticsEvent.Granola.ConnectClicked);
			try {
				await connect(apiKey);
				setApiKey('');
			} catch {
				setError('Failed to connect. Please check your API key and try again.');
			}
		},
		[connect, apiKey, track]
	);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Granola.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	if (isLoadingStatus) return null;

	const isConnected = status?.status === 'connected';

	return (
		<Card variant="outline" borderColor="border.soft" borderRadius="lg">
			<CardBody py={3} px={4}>
				<Flex alignItems="center" justifyContent="space-between">
					<VStack spacing={0} align="flex-start">
						<Text fontWeight="semibold" fontSize="sm" color="text.default">
							Granola
						</Text>
						<Text fontSize="xs" color="text.softer">
							{isConnected ? 'Connected' : 'Not connected'}
						</Text>
					</VStack>
					{isConnected ? (
						<Button size="sm" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
							Disconnect
						</Button>
					) : (
						<Flex as="form" onSubmit={handleConnect} alignItems="center" gap={2} flex={1} ml={4}>
							<Input type="password" size="sm" flex={1} placeholder="Granola API key (grn_...)" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
							<Button size="sm" colorScheme="primary" type="submit" isLoading={isConnecting} isDisabled={!apiKey.trim()} flexShrink={0}>
								Connect
							</Button>
						</Flex>
					)}
				</Flex>
				{error && (
					<Text fontSize="xs" color="text.danger" mt={2}>
						{error}
					</Text>
				)}
			</CardBody>
		</Card>
	);
};
